import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  RadioGroup,
  Radio,
  Card,
  CardContent,
  CardActionArea,
} from '@mui/material'
import { DarkMode, LightMode, SettingsBrightness } from '@mui/icons-material'
import { settingsApi } from '@api/endpoints/settings'
import type { UserPreferencesDto, Theme, Language, MeasurementUnit } from '@/types/settings'
import { ErrorDialog } from '@components/ErrorDialog'
import { getSaveFriendlyError, type FriendlyError } from '@utils/errorHandling'

export function PreferencesSection() {
  const [preferences, setPreferences] = useState<UserPreferencesDto | null>(null)
  const [theme, setTheme] = useState<Theme>('System' as Theme)
  const [language, setLanguage] = useState<Language>('en' as Language)
  const [measurementUnit, setMeasurementUnit] = useState<MeasurementUnit>(1) // Default to Imperial
  const [showLocationOnProfile, setShowLocationOnProfile] = useState(false)
  const [showOnlineStatus, setShowOnlineStatus] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<FriendlyError | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Load user preferences
  useEffect(() => {
    loadPreferences()
  }, [])

  const loadPreferences = async () => {
    try {
      setIsLoading(true)
      const data = await settingsApi.getUserPreferences()
      setPreferences(data)
      setTheme(data.theme)
      setLanguage(data.language)
      if (data.measurementUnit !== undefined) {
        setMeasurementUnit(data.measurementUnit)
      }
      setShowLocationOnProfile(data.showLocationOnProfile)
      setShowOnlineStatus(data.showOnlineStatus)
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)
      setError(null)
      setSuccessMessage(null)

      const response = await settingsApi.updateUserPreferences({
        theme,
        language,
        measurementUnit,
        showLocationOnProfile,
        showOnlineStatus,
      })

      if (response.success) {
        setSuccessMessage('Preferences updated successfully!')
        await loadPreferences()
      } else {
        throw new Error(response.message || 'Failed to update preferences')
      }
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* Error Dialog */}
      {error && (
        <ErrorDialog
          open={true}
          title={error.title}
          message={error.message}
          details={error.details}
          onClose={() => setError(null)}
        />
      )}

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Theme Selection */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Theme
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Choose your preferred theme. System will match your device settings.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Card
              variant="outlined"
              sx={{
                borderWidth: theme === 'Light' ? 2 : 1,
                borderColor: theme === 'Light' ? 'primary.main' : 'divider',
              }}
            >
              <CardActionArea onClick={() => setTheme('Light')}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <LightMode sx={{ fontSize: 40, mb: 1, color: 'warning.main' }} />
                  <Typography variant="h6">Light</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Bright theme
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card
              variant="outlined"
              sx={{
                borderWidth: theme === 'Dark' ? 2 : 1,
                borderColor: theme === 'Dark' ? 'primary.main' : 'divider',
              }}
            >
              <CardActionArea onClick={() => setTheme('Dark')}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <DarkMode sx={{ fontSize: 40, mb: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Dark</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Dark theme
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card
              variant="outlined"
              sx={{
                borderWidth: theme === 'System' ? 2 : 1,
                borderColor: theme === 'System' ? 'primary.main' : 'divider',
              }}
            >
              <CardActionArea onClick={() => setTheme('System')}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <SettingsBrightness sx={{ fontSize: 40, mb: 1, color: 'text.secondary' }} />
                  <Typography variant="h6">System</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Match device
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Language & Region */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Language & Region
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Language</InputLabel>
              <Select
                value={language}
                onChange={(e) => setLanguage(e.target.value as Language)}
                label="Language"
              >
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="es">Español</MenuItem>
                <MenuItem value="fr">Français</MenuItem>
                <MenuItem value="de">Deutsch</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Measurement System</InputLabel>
              <Select
                value={measurementUnit}
                onChange={(e) => setMeasurementUnit(Number(e.target.value) as MeasurementUnit)}
                label="Measurement System"
              >
                <MenuItem value={0}>Metric (km, meters, etc.)</MenuItem>
                <MenuItem value={1}>Imperial (miles, feet, etc.)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button variant="outlined" onClick={loadPreferences} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={16} /> : null}
        >
          {isSaving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </Box>
    </Box>
  )
}
